/**
 * SettingField — canonical labelled-form-row primitive.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.6 ("Detail / form sections"):
 *   - Use inside <SectionCard> (from ./section-card)
 *   - 2-column Label/Input grid in `max-w-2xl` form width
 *   - Every field shows "Last changed by X on Y" metadata
 *   - Icon + text on status indicators (no color-only)
 *   - Tooltips on icon buttons
 *
 * v1 source: ported from `components/admin/setting-field.tsx` which
 * grew up inside the admin module. PR-0 elevates it to the canonical
 * sysadmin primitive used across Phase 5 PR-A..F. The legacy admin
 * path remains as a re-export shim so existing imports keep working.
 *
 * Helper sub-components exported alongside:
 *   - <ChangedMeta>     "Last changed by X on Y" pill
 *   - <DangerZone>      bottom-of-page destructive-actions wrapper
 *   - <DangerAction>    a single row inside DangerZone
 *   - <StatusDot>       inline dot (healthy / degraded / failed / disabled / pending)
 *   - <VerifiedBadge>   inline tick / triangle "verified" badge
 *
 * @example
 * <SectionCard title="API Keys" description="Manage tenant API keys">
 *   <SettingField
 *     label="Default rate limit"
 *     description="Requests / second per key"
 *     changedBy="Dana Whitfield"
 *     changedAt="2026-05-10T12:00:00Z"
 *   >
 *     <Input type="number" defaultValue={100} />
 *   </SettingField>
 * </SectionCard>
 */

import { type ReactNode, type ComponentType } from 'react';
import { Pencil, Clock, Info, AlertTriangle, Lock, Check } from 'lucide-react';
import { Label } from './label';
import { Card, CardContent } from './card';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './tooltip';
import { Badge } from './badge';
import { cn } from '@/lib/utils';

// ─── SettingField ───────────────────────────────────────────────────────────

export interface SettingFieldProps {
  label: string;
  description?: string;
  required?: boolean;
  /**
   * The display name of the person who last changed the value, as the SERVER
   * named them (a settings response's `changedBy.name` — NamedUser,
   * userDisplayName). Never a user id: the browser builds no name (USR-F1-T4
   * AC-06). Omit it when there is no person; the chip then shows the time.
   */
  changedBy?: string;
  changedAt?: string;
  /** If true, field is shown but not editable by current role */
  lockedByPolicy?: boolean;
  lockedReason?: string;
  /** If true, value is read-only (e.g. tenant slug post-create) */
  readOnly?: boolean;
  readOnlyReason?: string;
  /** SLA impact note */
  slaImpact?: string;
  /** Compliance framework citation */
  complianceCitation?: string;
  className?: string;
  children: ReactNode;
}

export function SettingField({
  label, description, required, changedBy, changedAt, lockedByPolicy, lockedReason,
  readOnly, readOnlyReason, slaImpact, complianceCitation, className, children,
}: SettingFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-start justify-between gap-2">
        <Label className="text-xs font-semibold flex items-center gap-1.5">
          {label}
          {required && <span className="text-red-500">*</span>}
          {lockedByPolicy && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Lock className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-xs">
                  {lockedReason ?? 'Locked by platform policy'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {readOnly && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Lock className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="text-xs max-w-xs">
                  {readOnlyReason ?? 'Read-only after creation'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Label>
        {(changedBy || changedAt) && <ChangedMeta by={changedBy} at={changedAt} />}
      </div>

      {description && (
        <p className="text-[11px] text-muted-foreground leading-relaxed">{description}</p>
      )}

      <div className={cn(lockedByPolicy || readOnly ? 'opacity-60 pointer-events-none' : '')}>
        {children}
      </div>

      {slaImpact && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {slaImpact}
        </p>
      )}
      {complianceCitation && (
        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" /> {complianceCitation}
        </p>
      )}
    </div>
  );
}

// ─── ChangedMeta — "Last changed by X on Y" pill ────────────────────────────

/**
 * `by` is a person's display name computed by the server, shown as given —
 * never an id reshaped into something name-like (USR-F1-T4 AC-06). Without a
 * person the chip shows the time alone.
 */
export function ChangedMeta({ by, at, className }: { by?: string; at?: string; className?: string }) {
  if (!by && !at) return null;
  const when = at ? formatRelative(at) : null;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[10px] text-muted-foreground cursor-default',
              className,
            )}
          >
            <Pencil className="h-2.5 w-2.5" />
            {by && <span>{by}</span>}
            {when && <span>{by ? '· ' : ''}{when}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          {by && <div>Last changed by <strong>{by}</strong></div>}
          {at && <div>at {new Date(at).toLocaleString()}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── DangerZone — bottom-of-page destructive actions ──────────────────────

export function DangerZone({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('border-t-2 border-red-500/30 pt-6 mt-8', className)}>
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-300 uppercase tracking-wider">
          Danger zone
        </h3>
      </div>
      <Card className="border-red-500/40">
        <CardContent className="p-4 space-y-3">{children}</CardContent>
      </Card>
    </div>
  );
}

export function DangerAction({
  title, description, action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold">{title}</div>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

// ─── StatusDot — inline status indicator ────────────────────────────────────

export type SettingStatusDotStatus =
  | 'healthy'
  | 'degraded'
  | 'failed'
  | 'disabled'
  | 'pending';

export function StatusDot({
  status, className,
}: {
  status: SettingStatusDotStatus;
  className?: string;
}) {
  const colors: Record<SettingStatusDotStatus, string> = {
    healthy:  'bg-emerald-500',
    degraded: 'bg-amber-500',
    failed:   'bg-red-500',
    disabled: 'bg-muted-foreground/40',
    pending:  'bg-blue-500 animate-pulse',
  };
  return <span className={cn('h-1.5 w-1.5 rounded-full inline-block', colors[status], className)} />;
}

// ─── VerifiedBadge — shows verified/not with tick ─────────────────────────

export function VerifiedBadge({
  verified, label,
}: {
  verified: boolean;
  label: string;
}) {
  return (
    <Badge variant="outline" className={cn(
      'text-[10px] gap-1',
      verified
        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
        : 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    )}>
      {verified ? <Check className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
      {label}
    </Badge>
  );
}

// ─── Helper ─────────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return new Date(iso).toLocaleDateString();
}

// Re-export type from a sibling for ergonomic imports. The
// SectionCard primitive lives in its own file so consumers can pull
// it independently when they don't need SettingField semantics.
// (Avoids forcing 8 form deps just to render a card title bar.)
export { SectionCard, type SectionCardProps } from './section-card';

// Backwards-compat alias used by the legacy `components/admin/setting-field.tsx`
// shim and a few older call sites.
export type { SettingStatusDotStatus as StatusDotStatus };

// Public formatting helper — exposed for tests + custom callers (e.g. the
// "last changed" line in agent detail). Marked stable; we keep it
// alongside SettingField because both consumers benefit from a shared
// "how recent is this?" representation.
export const __formatRelativeForTests = formatRelative;
